import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  User,
  FileText,
  Bookmark,
  FileCheck,
  Briefcase,
  CreditCard,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  IndianRupee,
} from "lucide-react";
import { fetchBasicDetailsBySingleParamAsync, selectBasicDetailsBySingleParam } from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice";
import { useDispatch, useSelector } from "react-redux";
import { CANDIDATE_PROFILE } from "../../common/URLs";

const CandLgBar = () => {
  const [selectedLink, setSelectedLink] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // mobile open/close
  const [isCollapsed, setIsCollapsed] = useState(false); // desktop collapse
  const [isMobileView, setIsMobileView] = useState(false);
  const dispatch = useDispatch();
  const userdetails = useSelector(selectBasicDetailsBySingleParam);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch basic details (logic intact)
  const fetchUserDetails = useCallback(() => {
    dispatch(fetchBasicDetailsBySingleParamAsync());
  }, [dispatch]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobileView(mobile);
      if (mobile) setIsCollapsed(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine active link from path (logic intact)
  const getActiveLinkFromPath = (pathname) => {
    if (pathname === "/candidate-dashboard" || pathname === "/candidate-dashboard/") {
      return "DashHome";
    } else if (pathname.includes("/recommended-jobs")) {
      return "recommendedJobs";
    } else if (pathname.includes("/saved-jobs")) {
      return "savedJobs";
    } else if (pathname.includes("/applied-jobs")) {
      return "Candidate";
    } else if (pathname.includes("/resume")) {
      return "resume";
    } else if (pathname.includes("/subscription-plan")) {
      return "subscription-plan";
    } else if (pathname.includes("/subscription-billings")) {
      return "subscription-billings";
    } else if (pathname.includes("/interview-preparation")) {
      return "interview-preparation";
    } else if (pathname.includes("/skill-development")) {
      return "skill-development";
    } else if (pathname.includes("/career-advice")) {
      return "career-advice";
    } else if (pathname.includes("/support")) {
      return "support";
    }
    return "";
  };

  useEffect(() => {
    setSelectedLink(getActiveLinkFromPath(location.pathname));
  }, [location.pathname]);

  const handleClick = (link, path) => {
    setSelectedLink(link);
    navigate(path);
    if (isMobileView) {
      const evt = new CustomEvent("toggleSidebar", { detail: { isOpen: false } });
      document.dispatchEvent(evt);
      setIsSidebarOpen(false);
    }
  };

  const toggleCollapse = () => {
    if (!isMobileView) {
      const next = !isCollapsed;
      setIsCollapsed(next);
      const event = new CustomEvent('sidebarCollapseChange', {
        detail: { isCollapsed: next }
      });
      document.dispatchEvent(event);
    }
  };

  // Listen for navbar toggle events (mobile)
  useEffect(() => {
    const handleToggleEvent = (event) => {
      setIsSidebarOpen(event.detail.isOpen);
    };
    document.addEventListener('toggleSidebar', handleToggleEvent);
    return () => {
      document.removeEventListener('toggleSidebar', handleToggleEvent);
    };
  }, []);

  const menuItems = [
    { key: "DashHome", path: "/candidate-dashboard", icon: LayoutDashboard, text: "Candidate Profile" },
    { key: "recommendedJobs", path: "/candidate-dashboard/recommended-jobs", icon: Briefcase, text: "Recommended Jobs" },
    { key: "savedJobs", path: "/candidate-dashboard/saved-jobs", icon: Bookmark, text: "Saved Jobs" },
    { key: "Candidate", path: "/candidate-dashboard/applied-jobs", icon: FileCheck, text: "Applied Jobs" },
    { key: "resume", path: "/candidate-dashboard/resume", icon: FileText, text: "Resume Builder" },
    { key: "interview-preparation", path: "/candidate-dashboard/interview-preparation", icon: FileText, text: "interview-preparation" },
    { key: "skill-development", path: "/candidate-dashboard/skill-development", icon: FileText, text: "skill-development" },
    { key: "career-advice", path: "/candidate-dashboard/career-advice", icon: FileText, text: "career-advice" },
    // { key: "subscription-plan", path: "/candidate-dashboard/subscription-plan", icon: IndianRupee, text: "Subscription" },
    // { key: "subscription-billings", path: "/candidate-dashboard/subscription-billings", icon: CreditCard, text: "Billings" },
    { key: "support", path: "/candidate-dashboard/support", icon: HelpCircle, text: "Support" },
  ];

  const userData = userdetails;
  const profilePic = userdetails?.basic_details?.profile_pic;

  const desktopWidth = isCollapsed && !isMobileView ? "lg:w-20" : "lg:w-64";

  return (
    <aside
      className={[
        "cd-sidebar",
        "fixed left-0 top-14 md:top-16 lg:top-0 bottom-0",
        "transform transition-transform duration-300 ease-in-out",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full",
        "lg:translate-x-0",
        "bg-white border-r border-gray-200",
        "px-2 py-2 flex flex-col gap-2",
        "w-64", // base/mobile width
        desktopWidth, // desktop width
        "z-40",
        "overflow-y-auto",
      ].join(" ")}
      aria-hidden={!isSidebarOpen && isMobileView}
      role="navigation"
    >
      {/* User Profile Section */}
      <div className="flex items-center justify-between p-2 border-b border-gray-200 mb-2">
        {(!isCollapsed || isMobileView) && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              {profilePic ? (
                <img
                  src={`${CANDIDATE_PROFILE}/${profilePic}`}
                  alt="Profile"
                  className="w-8 h-8 rounded-full mr-2 object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                  <User className="text-gray-600" size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {userData?.name || "No Name"}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {userData?.email || "No Email"}
                </div>
              </div>
            </div>

            {/* Profile Completion (logic unchanged) */}
            <div className="mb-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Profile Completion</span>
                <span className="text-xs font-medium text-gray-900">
                  {userData?.profileCompletion || 90}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full"
                  style={{ width: `${userData?.profileCompletion || 90}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Collapse Button - Desktop only */}
        {!isMobileView && (
          <div className="hidden lg:block ml-2">
            <button
              onClick={toggleCollapse}
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="text-blue-700" size={24} />
              ) : (
                <ChevronLeft className="text-blue-700" size={24} />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto pb-6">
        <div className="space-y-1">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = selectedLink === item.key;
            return (
              <Link
                key={item.key}
                className={[
                  "rounded-lg flex items-center group transition-colors duration-200",
                  isCollapsed && !isMobileView ? "lg:justify-center lg:px-3 lg:py-3" : "px-3 py-3",
                  isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100",
                ].join(" ")}
                to={item.path}
                onClick={() => handleClick(item.key, item.path)}
                title={isCollapsed && !isMobileView ? item.text : ""}
              >
                <div className="flex items-center w-full">
                  <IconComponent
                    className={[
                      isCollapsed && !isMobileView ? "mx-auto" : "",
                      "flex-shrink-0",
                      isActive ? "text-white" : "text-gray-500 group-hover:text-gray-700",
                    ].join(" ")}
                    size={20}
                  />
                  {(!isCollapsed || isMobileView) && (
                    <span className="text-sm whitespace-nowrap overflow-hidden ml-3">
                      {item.text}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default CandLgBar;